import React, { useState, useEffect } from "react";
import {  Button,
  Typography,
  Box,
  Paper,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Print as PrintIcon,
  Inventory,
  LocationOn,
  Description,
  Speed,
  Business,
} from "@mui/icons-material";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useCompanyContext } from "../../Contexts/CompanyContext";
import { useTranslation } from "react-i18next";
import { renderRcsV2 } from "../../print-v2";
import { getSelectedCargo, getSelectedTrip } from "../../utils/voyageSelection";
import { getVoyageNumber } from "../../utils/domainSelectors";

const ReceivingCapacityStatement = () => {
  const { t } = useTranslation("receivingcapacitystatement");
  const theme = useTheme();
  const { selectedTripId, selectedCargoIndex, currentVoyage } =
    useVoyageContextV2();
  const { companyLogo } = useCompanyContext();

  const [inputRate, setInputRate] = useState("");
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  useEffect(() => {
    if (currentVoyage) setLoading(false);
  }, [currentVoyage]);

  const handlePrintButton = () => {
    const cargo = getSelectedCargo(
      currentVoyage,
      selectedTripId,
      selectedCargoIndex
    );

    if (!cargo) {
      setSnackbar({
        open: true,
        message: "Please select a cargo before printing.",
        severity: "warning",
      });
      return;
    }
    if (!inputRate) {
      setSnackbar({
        open: true,
        message: "Please set receiving rate before printing.",
        severity: "warning",
      });
      return;
    }

    renderRcsV2({
      currentVoyage,
      selectedTripId,
      selectedCargoIndex,
      inputRate,
      companyLogo,
      t,
    });
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );

  const trip = getSelectedTrip(currentVoyage, selectedTripId);
  const cargo = getSelectedCargo(
    currentVoyage,
    selectedTripId,
    selectedCargoIndex
  );

  const InfoItem = ({ icon, label, value }) => (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <Box sx={{ color: "primary.main", mt: 0.5 }}>
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ fontWeight: 700, textTransform: "uppercase" }}
        >
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="600">
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", pb: 5 }}>
      <Typography variant="h4" fontWeight="900" sx={{ mb: 1 }}>
        {t("title")}
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
        Declare the vessel's receiving capacity and maximum safe loading rate.
      </Typography>

      <Stack spacing={3}>
        {/* 1. Voyage Summary */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <InfoItem
                icon={<Business />}
                label={t("voyageInfo.shippers")}
                value={trip?.supplierReceiver}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <InfoItem
                icon={<LocationOn />}
                label={t("voyageInfo.port")}
                value={trip?.to}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <InfoItem
                icon={<Description />}
                label={t("voyageInfo.voyage")}
                value={getVoyageNumber(currentVoyage)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <InfoItem
                icon={<Inventory />}
                label={t("voyageInfo.grade")}
                value={`${cargo?.cargo} (${cargo?.quantity} ${cargo?.unit})`}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* 2. Rate Input Section */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Speed fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="800">
                LOADING PARAMETERS
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: "100%", maxWidth: 400 }}>
              <Typography
                variant="body2"
                fontWeight="700"
                sx={{ mb: 1, textAlign: "center" }}
              >
                {t("voyageInfo.rate")}
              </Typography>
              <TextField
                type="number"
                variant="outlined"
                fullWidth
                placeholder="Enter rate..."
                value={inputRate}
                onChange={(e) => setInputRate(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">m³/h</InputAdornment>
                  ),
                  sx: { borderRadius: 3, fontWeight: 700, fontSize: "1.2rem" },
                }}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 1, display: "block", textAlign: "center" }}
              >
                Specify the maximum requested loading rate for this operation.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Stack>

      {/* Action Button */}
      <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<PrintIcon />}
          onClick={handlePrintButton}
          sx={{
            py: 2,
            px: 10,
            borderRadius: 3,
            fontWeight: 800,
            fontSize: "1.1rem",
          }}
        >
          {t("voyageInfo.printRCS")}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReceivingCapacityStatement;
